import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, Tag, Save, Plus, Edit2, Trash2, X, Check,
  AlertCircle, Crown, Percent, Calendar, RefreshCw, Mail, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyPortalPricing = () => {
  const [activeTab, setActiveTab] = useState('hiring');
  
  // Hiring Challenge Pricing State
  const [pricing, setPricing] = useState({
    national_challenge_price: 9999,
    featured_job_price: 0,
    ai_screening_price: 0
  });
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  
  // Email Pricing State
  const [emailPackages, setEmailPackages] = useState([
    { id: 'basic', name: 'Basic', emails: 200, price: 499, description: '1-200 emails' },
    { id: 'professional', name: 'Professional', emails: 1000, price: 999, description: '201-1000 emails' }
  ]);
  const [emailUsageStats, setEmailUsageStats] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  
  // Coupon form
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: '',
    expiry_date: ''
  });

  // Email package form
  const [showEmailPackageModal, setShowEmailPackageModal] = useState(false);
  const [editingEmailPackage, setEditingEmailPackage] = useState(null);
  const [emailPackageForm, setEmailPackageForm] = useState({
    id: '',
    name: '',
    emails: 100,
    price: 299,
    description: ''
  });

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchPricing();
    fetchEmailPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/settings/company-pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.pricing) {
        setPricing(res.data.pricing);
      }
      setCoupons(res.data.coupons || []);
      setLastUpdated(res.data.updated_at);
      setUpdatedBy(res.data.updated_by);
    } catch (err) {
      toast.error('Failed to fetch pricing settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailPricing = async () => {
    try {
      setEmailLoading(true);
      const [pricingRes, usageRes] = await Promise.all([
        axios.get(`${API}/admin/settings/email-pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/email-usage`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      if (pricingRes.data.packages) {
        setEmailPackages(pricingRes.data.packages);
      }
      if (usageRes.data) {
        setEmailUsageStats(usageRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch email pricing');
    } finally {
      setEmailLoading(false);
    }
  };

  const savePricing = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/settings/company-pricing`, pricing, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pricing updated successfully!');
      fetchPricing();
    } catch (err) {
      toast.error('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  const saveEmailPricing = async () => {
    try {
      setEmailSaving(true);
      await axios.put(`${API}/admin/settings/email-pricing`, { packages: emailPackages }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email pricing updated successfully!');
      fetchEmailPricing();
    } catch (err) {
      toast.error('Failed to update email pricing');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleCreateCoupon = async () => {
    if (!couponForm.code.trim()) {
      toast.error('Coupon code is required');
      return;
    }
    
    try {
      const payload = {
        code: couponForm.code.toUpperCase(),
        discount_type: couponForm.discount_type,
        discount_value: parseFloat(couponForm.discount_value) || 0,
        max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
        expiry_date: couponForm.expiry_date || null
      };
      
      if (editingCoupon) {
        await axios.put(`${API}/admin/settings/company-coupons/${editingCoupon.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coupon updated');
      } else {
        await axios.post(`${API}/admin/settings/company-coupons`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coupon created');
      }
      
      setShowCouponModal(false);
      resetCouponForm();
      fetchPricing();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;
    
    try {
      await axios.delete(`${API}/admin/settings/company-coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon deleted');
      fetchPricing();
    } catch (err) {
      toast.error('Failed to delete coupon');
    }
  };

  const toggleCouponStatus = async (coupon) => {
    try {
      await axios.put(`${API}/admin/settings/company-coupons/${coupon.id}`, {
        ...coupon,
        is_active: !coupon.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Coupon ${!coupon.is_active ? 'activated' : 'deactivated'}`);
      fetchPricing();
    } catch (err) {
      toast.error('Failed to update coupon');
    }
  };

  const resetCouponForm = () => {
    setCouponForm({
      code: '',
      discount_type: 'percentage',
      discount_value: 10,
      max_uses: '',
      expiry_date: ''
    });
    setEditingCoupon(null);
  };

  const openEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      max_uses: coupon.max_uses || '',
      expiry_date: coupon.expiry_date ? coupon.expiry_date.split('T')[0] : ''
    });
    setShowCouponModal(true);
  };

  const handleSaveEmailPackage = () => {
    if (!emailPackageForm.name.trim() || !emailPackageForm.id.trim()) {
      toast.error('Package ID and name are required');
      return;
    }

    const updatedPackages = [...emailPackages];
    
    if (editingEmailPackage) {
      const idx = updatedPackages.findIndex(p => p.id === editingEmailPackage.id);
      if (idx !== -1) {
        updatedPackages[idx] = {
          id: emailPackageForm.id.toLowerCase().replace(/\s+/g, '_'),
          name: emailPackageForm.name,
          emails: parseInt(emailPackageForm.emails) || 100,
          price: parseInt(emailPackageForm.price) || 0,
          description: emailPackageForm.description
        };
      }
    } else {
      updatedPackages.push({
        id: emailPackageForm.id.toLowerCase().replace(/\s+/g, '_'),
        name: emailPackageForm.name,
        emails: parseInt(emailPackageForm.emails) || 100,
        price: parseInt(emailPackageForm.price) || 0,
        description: emailPackageForm.description
      });
    }

    setEmailPackages(updatedPackages);
    setShowEmailPackageModal(false);
    resetEmailPackageForm();
    toast.success(editingEmailPackage ? 'Package updated' : 'Package added');
  };

  const resetEmailPackageForm = () => {
    setEmailPackageForm({
      id: '',
      name: '',
      emails: 100,
      price: 299,
      description: ''
    });
    setEditingEmailPackage(null);
  };

  const openEditEmailPackage = (pkg) => {
    setEditingEmailPackage(pkg);
    setEmailPackageForm({
      id: pkg.id,
      name: pkg.name,
      emails: pkg.emails,
      price: pkg.price,
      description: pkg.description || ''
    });
    setShowEmailPackageModal(true);
  };

  const deleteEmailPackage = (pkgId) => {
    if (!window.confirm('Delete this package?')) return;
    setEmailPackages(prev => prev.filter(p => p.id !== pkgId));
    toast.success('Package removed (save to apply)');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-purple-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Company Portal Pricing</h2>
          <p className="text-gray-500">Manage pricing for hiring challenges and email credits</p>
        </div>
        {lastUpdated && (
          <div className="text-sm text-gray-500">
            Last updated: {new Date(lastUpdated).toLocaleString()} by {updatedBy}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('hiring')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'hiring' 
              ? 'border-amber-500 text-amber-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-hiring"
        >
          <Crown size={16} className="inline mr-2" />
          Hiring Challenges
        </button>
        <button
          onClick={() => setActiveTab('email')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'email' 
              ? 'border-green-500 text-green-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-email"
        >
          <Mail size={16} className="inline mr-2" />
          Email Credits
        </button>
      </div>

      {/* Hiring Challenges Tab */}
      {activeTab === 'hiring' && (
        <div className="space-y-8">
          {/* Pricing Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Crown size={20} />
                Pricing Configuration
              </h3>
            </div>
            
            <div className="p-6 space-y-6">
              {/* National Challenge Price */}
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Crown className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">National Level Hiring Challenge</h4>
                    <p className="text-sm text-gray-500">Featured placement + Pan-India visibility</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500">₹</span>
                  <input
                    type="number"
                    value={pricing.national_challenge_price}
                    onChange={(e) => setPricing(p => ({ ...p, national_challenge_price: parseInt(e.target.value) || 0 }))}
                    className="w-32 px-4 py-2 border border-amber-300 rounded-lg text-xl font-bold text-amber-600 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    data-testid="national-challenge-price-input"
                  />
                </div>
              </div>

              {/* Other Pricing Options */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="text-gray-600" size={20} />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">Featured Job Posting</h4>
                      <p className="text-xs text-gray-500">Premium visibility for job posts</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">₹</span>
                    <input
                      type="number"
                      value={pricing.featured_job_price}
                      onChange={(e) => setPricing(p => ({ ...p, featured_job_price: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="text-gray-600" size={20} />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">AI Screening (per candidate)</h4>
                      <p className="text-xs text-gray-500">AI-powered resume screening</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">₹</span>
                    <input
                      type="number"
                      value={pricing.ai_screening_price}
                      onChange={(e) => setPricing(p => ({ ...p, ai_screening_price: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={savePricing}
                disabled={saving}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-amber-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-pricing-btn"
              >
                {saving ? <RefreshCw className="animate-spin" size={20} /> : <Save size={20} />}
                Save Pricing
              </button>
            </div>
          </div>

          {/* Coupons Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Tag size={20} />
                Company Coupon Codes
              </h3>
              <button
                onClick={() => { resetCouponForm(); setShowCouponModal(true); }}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
                data-testid="create-coupon-btn"
              >
                <Plus size={18} />
                Create Coupon
              </button>
            </div>
            
            <div className="p-6">
              {coupons.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Tag size={48} className="mx-auto mb-3 opacity-30" />
                  <p>No coupons created yet</p>
                  <p className="text-sm">Create coupons to offer discounts to companies</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {coupons.map((coupon) => (
                    <div 
                      key={coupon.id} 
                      className={`p-4 rounded-xl border ${coupon.is_active ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`px-4 py-2 rounded-lg font-mono font-bold ${coupon.is_active ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-500'}`}>
                            {coupon.code}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              {coupon.discount_type === 'percentage' ? (
                                <span className="flex items-center gap-1 text-purple-600 font-semibold">
                                  <Percent size={16} />
                                  {coupon.discount_value}% off
                                </span>
                              ) : (
                                <span className="text-purple-600 font-semibold">
                                  ₹{coupon.discount_value} off
                                </span>
                              )}
                              {!coupon.is_active && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-500 text-xs rounded">Disabled</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                              {coupon.max_uses && (
                                <span>Uses: {coupon.current_uses || 0}/{coupon.max_uses}</span>
                              )}
                              {coupon.expiry_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={12} />
                                  Expires: {new Date(coupon.expiry_date).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleCouponStatus(coupon)}
                            className={`p-2 rounded-lg transition-colors ${
                              coupon.is_active 
                                ? 'bg-gray-100 hover:bg-gray-200 text-gray-600' 
                                : 'bg-green-100 hover:bg-green-200 text-green-600'
                            }`}
                            title={coupon.is_active ? 'Disable' : 'Enable'}
                          >
                            {coupon.is_active ? <X size={16} /> : <Check size={16} />}
                          </button>
                          <button
                            onClick={() => openEditCoupon(coupon)}
                            className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteCoupon(coupon.id)}
                            className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Credits Tab */}
      {activeTab === 'email' && (
        <div className="space-y-8">
          {/* Usage Stats */}
          {emailUsageStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Mail className="text-green-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Emails Sent</p>
                    <p className="text-2xl font-bold text-gray-900">{emailUsageStats.total_emails_sent}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Package className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Credits Purchased</p>
                    <p className="text-2xl font-bold text-gray-900">{emailUsageStats.total_credits_purchased}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Check className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Credits Used</p>
                    <p className="text-2xl font-bold text-gray-900">{emailUsageStats.total_credits_used}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Companies with Credits</p>
                    <p className="text-2xl font-bold text-gray-900">{emailUsageStats.companies_with_credits}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Email Packages */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Package size={20} />
                Email Credit Packages
              </h3>
              <button
                onClick={() => { resetEmailPackageForm(); setShowEmailPackageModal(true); }}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
                data-testid="add-email-package-btn"
              >
                <Plus size={18} />
                Add Package
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-3">
                {emailPackages.map((pkg) => (
                  <div 
                    key={pkg.id} 
                    className="p-4 rounded-xl border-2 border-green-200 bg-green-50"
                    data-testid={`email-package-${pkg.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                          <Mail className="text-green-600" size={24} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{pkg.name}</h4>
                          <p className="text-sm text-gray-500">{pkg.description}</p>
                          <p className="text-xs text-green-600 mt-1">Up to {pkg.emails} emails</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">₹{pkg.price}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditEmailPackage(pkg)}
                            className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => deleteEmailPackage(pkg.id)}
                            className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={saveEmailPricing}
                disabled={emailSaving}
                className="w-full mt-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-green-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-email-pricing-btn"
              >
                {emailSaving ? <RefreshCw className="animate-spin" size={20} /> : <Save size={20} />}
                Save Email Pricing
              </button>
            </div>
          </div>

          {/* Info Alert */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <AlertCircle className="text-blue-500 flex-shrink-0" size={20} />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Email Credit Pricing</p>
              <p>Companies can purchase email credits to send bulk emails to shortlisted candidates. The default packages are:</p>
              <ul className="list-disc list-inside mt-2">
                <li><strong>Basic (₹499):</strong> 1-200 emails</li>
                <li><strong>Professional (₹999):</strong> 201-1000 emails</li>
              </ul>
              <p className="mt-2">Changes to pricing will be reflected immediately on the Company Portal.</p>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Modal */}
      {showCouponModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4 rounded-t-2xl">
              <h3 className="text-lg font-semibold text-white">
                {editingCoupon ? 'Edit Coupon' : 'Create Coupon'}
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code *</label>
                <input
                  type="text"
                  value={couponForm.code}
                  onChange={(e) => setCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono"
                  placeholder="e.g., COMPANY20"
                  data-testid="coupon-code-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                  <select
                    value={couponForm.discount_type}
                    onChange={(e) => setCouponForm(f => ({ ...f, discount_type: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                  <input
                    type="number"
                    value={couponForm.discount_value}
                    onChange={(e) => setCouponForm(f => ({ ...f, discount_value: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    min="0"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses (Optional)</label>
                  <input
                    type="number"
                    value={couponForm.max_uses}
                    onChange={(e) => setCouponForm(f => ({ ...f, max_uses: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (Optional)</label>
                  <input
                    type="date"
                    value={couponForm.expiry_date}
                    onChange={(e) => setCouponForm(f => ({ ...f, expiry_date: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setShowCouponModal(false); resetCouponForm(); }}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCoupon}
                  className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg transition-all"
                  data-testid="save-coupon-btn"
                >
                  {editingCoupon ? 'Update' : 'Create'} Coupon
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Package Modal */}
      {showEmailPackageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 rounded-t-2xl">
              <h3 className="text-lg font-semibold text-white">
                {editingEmailPackage ? 'Edit Package' : 'Add Package'}
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package ID *</label>
                  <input
                    type="text"
                    value={emailPackageForm.id}
                    onChange={(e) => setEmailPackageForm(f => ({ ...f, id: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., premium"
                    disabled={editingEmailPackage}
                    data-testid="email-package-id-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package Name *</label>
                  <input
                    type="text"
                    value={emailPackageForm.name}
                    onChange={(e) => setEmailPackageForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., Premium"
                    data-testid="email-package-name-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Count</label>
                  <input
                    type="number"
                    value={emailPackageForm.emails}
                    onChange={(e) => setEmailPackageForm(f => ({ ...f, emails: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    min="1"
                    data-testid="email-package-count-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                  <input
                    type="number"
                    value={emailPackageForm.price}
                    onChange={(e) => setEmailPackageForm(f => ({ ...f, price: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    min="0"
                    data-testid="email-package-price-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={emailPackageForm.description}
                  onChange={(e) => setEmailPackageForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., 1-200 emails"
                  data-testid="email-package-desc-input"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setShowEmailPackageModal(false); resetEmailPackageForm(); }}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEmailPackage}
                  className="flex-1 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium hover:shadow-lg transition-all"
                  data-testid="save-email-package-btn"
                >
                  {editingEmailPackage ? 'Update' : 'Add'} Package
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPortalPricing;
